import React, { useState, useEffect, useRef } from 'react';
import { ColumnDataType } from '../../types/grid';
import { Checkbox } from '../ui/Checkbox';
import { Input } from '../ui/Input';

interface InlineCellEditorProps {
  initialValue: any;
  dataType: ColumnDataType;
  onCommit: (newValue: any) => void;
  onCancel: () => void;
  onNavigate?: (direction: 'next' | 'prev' | 'down' | 'up') => void;
}

export const InlineCellEditor: React.FC<InlineCellEditorProps> = ({
  initialValue,
  dataType,
  onCommit,
  onCancel,
  onNavigate,
}) => {
  const [value, setValue] = useState(() => {
    if (initialValue === null || initialValue === undefined) return '';
    if (typeof initialValue === 'object') return JSON.stringify(initialValue);
    return String(initialValue);
  });

  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    inputRef.current?.focus();
    inputRef.current?.select();
  }, []);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      commitAndExit();
      onNavigate?.('down');
    } else if (e.key === 'Tab') {
      e.preventDefault();
      commitAndExit();
      onNavigate?.(e.shiftKey ? 'prev' : 'next');
    } else if (e.key === 'Escape') {
      e.preventDefault();
      onCancel();
    }
  };

  const commitAndExit = () => {
    let parsedValue: any = value;
    if (dataType === 'number') {
      const num = parseFloat(value);
      parsedValue = isNaN(num) ? value : num;
    } else if (dataType === 'boolean') {
      parsedValue = value === 'true' || value === '1' || value === 'yes';
    } else if (dataType === 'json') {
      try {
        parsedValue = JSON.parse(value);
      } catch {
        parsedValue = value;
      }
    }
    onCommit(parsedValue);
  };

  if (dataType === 'boolean') {
    return (
      <div className="flex items-center justify-center w-full h-full bg-cf-surfaceActive px-2">
        <Checkbox
          ref={inputRef}
          checked={value === 'true'}
          onChange={(e) => {
            const nextBool = e.target.checked;
            setValue(String(nextBool));
            onCommit(nextBool);
          }}
          onBlur={commitAndExit}
          onKeyDown={handleKeyDown}
        />
      </div>
    );
  }

  return (
    <Input
      ref={inputRef}
      type={dataType === 'number' ? 'number' : 'text'}
      value={value}
      onChange={(e) => setValue(e.target.value)}
      onBlur={commitAndExit}
      onKeyDown={handleKeyDown}
      className="w-full h-full px-2 py-0 border-2 border-cf-accent text-xs shadow-sm z-10"
    />
  );
};
